/**
 * useAccessControl Hook
 *
 * Provides access control checks based on the user's module permissions.
 * Reads from Redux state (auth.accessControl), which is populated on login
 * and restored from localStorage on page refresh.
 *
 * Usage:
 *   const { hasModuleAccess, canCreate, canRead } = useAccessControl();
 *   if (hasModuleAccess('USERS')) { ... }
 */

import React, { useCallback } from 'react';
import { useAppSelector } from '@store/hooks';

export const useAccessControl = () => {
  const accessControl = useAppSelector((state) => state.auth.accessControl);

  // DEBUG: Log access control data
  React.useEffect(() => {
    // console.log('[useAccessControl] Access control data:', accessControl);
  }, [accessControl]);

  /**
   * Find the permission entry for a given module code
   */
  const getModulePermissions = useCallback(
    (moduleCode) => {
      if (!accessControl || !Array.isArray(accessControl)) return null;
      const permission = accessControl.find((m) => m.module_code === moduleCode) || null;

      // DEBUG: Log permission lookup
      if (moduleCode === 'CMS') {
        // console.log('[useAccessControl] CMS permission:', permission);
      }

      return permission;
    },
    [accessControl]
  );

  /**
   * Check if a module is accessible (visible in sidebar / routes).
   * Returns true only if module is enabled AND user has read access.
   */
  const hasModuleAccess = useCallback(
    (moduleCode) => {
      if (!moduleCode) return true; // No module code = always visible (e.g., Dashboard)
      const perm = getModulePermissions(moduleCode);
      if (!perm) return false; // Module not found in permissions = deny access
      return perm.is_enabled && perm.read_access; // Check both is_enabled and read_access
    },
    [getModulePermissions]
  );

  /**
   * CRUD permission checks
   */
  const canCreate = useCallback(
    (moduleCode) => {
      const perm = getModulePermissions(moduleCode);
      return perm ? perm.is_enabled && perm.create_access : true;
    },
    [getModulePermissions]
  );

  const canRead = useCallback(
    (moduleCode) => {
      const perm = getModulePermissions(moduleCode);
      return perm ? perm.is_enabled && perm.read_access : true;
    },
    [getModulePermissions]
  );

  const canUpdate = useCallback(
    (moduleCode) => {
      const perm = getModulePermissions(moduleCode);
      return perm ? perm.is_enabled && perm.update_access : true;
    },
    [getModulePermissions]
  );

  const canDelete = useCallback(
    (moduleCode) => {
      const perm = getModulePermissions(moduleCode);
      return perm ? perm.is_enabled && perm.delete_access : true;
    },
    [getModulePermissions]
  );

  return {
    accessControl,
    getModulePermissions,
    hasModuleAccess,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
  };
};

export default useAccessControl;
